import type { Attraction } from './attractionService';
import type { TripPhoto } from './lifeDataService';

export interface TripAttraction extends Attraction {
  date:         string;    // "YYYY-MM-DD" — which day to visit
  transport?:   string;    // key from TRANSPORT_OPTIONS
  notes?:       string;
  order?:       number;    // sequence within the day (1-based)
  participants?: number;   // number of participants for this stop
  country?:     string;    // resolved from Nominatim
  city?:        string;    // resolved from Nominatim
  photos?:      TripPhoto[];  // 照片：url 字串（舊）或 {url, file_object_id} dict（Task-32 後）
}

export interface TripPlan {
  id: string;
  name: string;
  attractions: TripAttraction[];
  createdAt: number;
  coverPhotoUrl?: TripPhoto;  // 封面照片：url 字串（舊）或 {url, file_object_id} dict
}

/**
 * Normalize raw DB data to unified TripPlan format.
 * Handles both the current format (attractions[]) and the legacy TripPlannerPage
 * format (stops[]) so old plans migrate transparently on first load.
 */
export function normalizeTrips(raw: any[]): TripPlan[] {
  return (raw ?? []).map((p: any): TripPlan => {
    if (Array.isArray(p.attractions)) return p as TripPlan;
    // Convert legacy stops[] format
    return {
      id:          p.id,
      name:        p.name,
      createdAt:   p.createdAt ?? Date.now(),
      attractions: (p.stops ?? []).map((s: any, i: number): TripAttraction => ({
        id:          s.attraction?.osmId ?? `stop_${i}`,
        nameZH:      s.attraction?.nameZH ?? '',
        nameEN:      s.attraction?.nameEN ?? '',
        descZH:      '',
        descEN:      '',
        lat:         s.attraction?.lat ?? 0,
        lng:         s.attraction?.lng ?? 0,
        imageUrl:    s.attraction?.wikiImageUrl,
        wikidataUrl: s.attraction?.wikiPageUrl ?? '',
        date:        s.date ?? new Date().toISOString().slice(0, 10),
        transport:   undefined,
        notes:       s.notes ?? '',
      })),
    };
  });
}

export const TRANSPORT_OPTIONS = [
  { key: 'walk',  emoji: '🚶', zh: '步行',   en: 'Walk'  },
  { key: 'car',   emoji: '🚗', zh: '開車',   en: 'Car'   },
  { key: 'bus',   emoji: '🚌', zh: '公車',   en: 'Bus'   },
  { key: 'metro', emoji: '🚇', zh: '捷運',   en: 'Metro' },
  { key: 'train', emoji: '🚂', zh: '火車',   en: 'Train' },
  { key: 'plane', emoji: '✈️', zh: '飛機',   en: 'Plane' },
  { key: 'boat',  emoji: '⛵', zh: '船',     en: 'Boat'  },
  { key: 'bike',  emoji: '🚲', zh: '騎車',   en: 'Bike'  },
  { key: 'taxi',  emoji: '🚕', zh: '計程車', en: 'Taxi'  },
] as const;
